import React, { useState, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';

const cn = (...c) => c.filter(Boolean).join(' ');

function Field({ label, children }) {
  return (
    <label className="block text-xs font-medium space-y-1">
      <span>{label}</span>
      {children}
    </label>
  );
}

export default function AuthPanel({ className='' }) {
  const { signIn, signUp, error } = useAuth();
  const [mode, setMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setMessage('');
    setPending(true);
    try {
      if (mode === 'signin') {
        await signIn({ email, password });
      } else {
        await signUp({ email, password });
        setMessage('Account created. Check your email for confirmation (if enabled).');
      }
    } catch (e2) {
      // handled by context error
    } finally {
      setPending(false);
    }
  };

  const helpful = useMemo(() => {
    if (!error) return null;
    const lower = error.toLowerCase();
    if (lower.includes('invalid login credentials')) {
      return 'Those credentials are not in Supabase yet. Create a new account below (old Firebase accounts are not auto-migrated).';
    }
    if (lower.includes('email not confirmed')) {
      return 'Email not confirmed. Check your inbox (and spam). You can resend by attempting sign in again.';
    }
    return null;
  }, [error]);

  return (
    <div className={cn('max-w-sm mx-auto rounded-2xl border bg-elevated/40 p-6 shadow-sm', className)}>
      <h2 className="text-lg font-semibold mb-1">{mode === 'signin' ? 'Sign In' : 'Create Account'}</h2>
      <p className="text-xs text-secondary mb-4">{mode === 'signin' ? 'Access your pallet classification workspace.' : 'Create a new account to start classifying pallets.'}</p>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Email">
          <input type="email" required value={email} onChange={e=>setEmail(e.target.value)} className="w-full rounded-md border bg-surface px-3 py-2 text-sm" placeholder="you@example.com" />
        </Field>
        <Field label="Password">
          <input type="password" required value={password} onChange={e=>setPassword(e.target.value)} className="w-full rounded-md border bg-surface px-3 py-2 text-sm" placeholder="••••••••" />
        </Field>
        {error && <p className="text-xs text-red-500" role="alert">{error}</p>}
        {helpful && <p className="text-[10px] text-amber-600" role="note">{helpful}</p>}
        {message && <p className="text-xs text-green-600">{message}</p>}
        <button disabled={pending} className="w-full rounded-md border border-transparent bg-[color:var(--lodia-brand)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50">{pending ? 'Please wait…' : (mode === 'signin' ? 'Sign In' : 'Sign Up')}</button>
      </form>
      <div className="mt-4 text-center">
        <button onClick={()=> setMode(m => m === 'signin' ? 'signup' : 'signin')} className="text-xs text-[color:var(--lodia-brand)] underline">
          {mode === 'signin' ? 'Need an account? Sign up' : 'Already have an account? Sign in'}
        </button>
      </div>
      <div className="mt-5 rounded-md border border-dashed p-3 bg-surface/40">
        <p className="text-[10px] leading-relaxed text-secondary">
          Migration note: Firebase users aren\'t auto-imported. Use the Sign Up tab once per email. If you plan to migrate later, you can export Firebase auth users and import them into Supabase via the Admin API.
        </p>
      </div>
    </div>
  );
}
